import { omit } from 'lodash';
import * as Yup from 'yup';
import { formatPrice, formatPriceInput } from '@dashboard/services/formatting';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import STRIPE_PAYMENT_METHODS from '@dashboard/enums/stripe-payment-methods';

export const paymentMethods = [
  { value: STRIPE_PAYMENT_METHODS.AFFIRM, name: 'Affirm' },
  { value: STRIPE_PAYMENT_METHODS.AFTERPAY_CLEARPAY, name: 'Afterpay & Clearpay' },
  { value: STRIPE_PAYMENT_METHODS.ALIPAY, name: 'Alipay' },
  { value: STRIPE_PAYMENT_METHODS.AMAZON_PAY, name: 'Amazon Pay' },
  { value: STRIPE_PAYMENT_METHODS.AU_BECS_DEBIT, name: 'BECS Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.BACS_DEBIT, name: 'BACS Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.BANCONTACT, name: 'Bancontact' },
  { value: STRIPE_PAYMENT_METHODS.BLIK, name: 'Blik' },
  { value: STRIPE_PAYMENT_METHODS.BOLETO, name: 'Boleto' },
  { value: STRIPE_PAYMENT_METHODS.ACSS_DEBIT, name: 'Canadian pre-authorized debit payment' },
  { value: STRIPE_PAYMENT_METHODS.CARD, name: 'Card' },
  { value: STRIPE_PAYMENT_METHODS.CASHAPP, name: 'Cash App' },
  { value: STRIPE_PAYMENT_METHODS.EPS, name: 'EPS' },
  { value: STRIPE_PAYMENT_METHODS.FPX, name: 'FPX' },
  { value: STRIPE_PAYMENT_METHODS.GIROPAY, name: 'Giropay' },
  { value: STRIPE_PAYMENT_METHODS.GRABPAY, name: 'GrabPay' },
  { value: STRIPE_PAYMENT_METHODS.IDEAL, name: 'iDEAL' },
  { value: STRIPE_PAYMENT_METHODS.KLARNA, name: 'Klarna' },
  { value: STRIPE_PAYMENT_METHODS.LINK, name: 'Link' },
  { value: STRIPE_PAYMENT_METHODS.MOBILEPAY, name: 'MobilePay' },
  { value: STRIPE_PAYMENT_METHODS.MULTIBANCO, name: 'Multibanco' },
  { value: STRIPE_PAYMENT_METHODS.OXXO, name: 'Oxxo' },
  { value: STRIPE_PAYMENT_METHODS.P24, name: 'P24' },
  { value: STRIPE_PAYMENT_METHODS.PAYNOW, name: 'PayNow' },
  { value: STRIPE_PAYMENT_METHODS.PROMPTPAY, name: 'PromptPay' },
  { value: STRIPE_PAYMENT_METHODS.REVOLUT_PAY, name: 'Revolut Pay' },
  { value: STRIPE_PAYMENT_METHODS.SEPA_DEBIT, name: 'SEPA Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.SOFORT, name: 'Sofort' },
  { value: STRIPE_PAYMENT_METHODS.SWISH, name: 'Swish' },
  { value: STRIPE_PAYMENT_METHODS.TWINT, name: 'TWINT' },
  { value: STRIPE_PAYMENT_METHODS.US_BANK_ACCOUNT, name: 'ACH Direct Debit' },
  { value: STRIPE_PAYMENT_METHODS.WECHAT_PAY, name: 'WeChat Pay' },
  { value: STRIPE_PAYMENT_METHODS.ZIP, name: 'Zip' },
];

export const feeOptionShape = {
  name: '',
  amount: '',
  percentage: '',
  multiplyByQuantity: false,
  multiplyByTickets: false,
  applyToSpecificPaymentMethods: false,
  paymentMethods: [],
};

export const PERCENTAGE_OR_AMOUNT = 'percentageOrAmount';

export const EDITOR_KEYS = ['key', 'isNew', 'isEditing', 'type', PERCENTAGE_OR_AMOUNT];

export const isFilled = value => value !== '' && value !== null && value !== undefined;

// TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.currency)
export const getPageCurrency = page => page.product?.currency || page.eventDetails?.currency;

// Matches the zero-decimal currencies the price formatter knows (formatting/price.js).
export const ZERO_DECIMAL_CURRENCIES = ['jpy', 'clp'];

export const feeId = fee => fee._id || fee.key;

export function getFeeFormValues(fee, page) {
  return {
    ...omit(fee, EDITOR_KEYS),
    amount: fee.amount
      ? formatPriceForInput({
          amount: fee.amount,
          currency: getPageCurrency(page),
          style: 'decimal',
        })
      : '',
    percentage: fee.percentage ? fee.percentage : '',
    // Formik only marks keys in values as touched on submit; without it the error never shows.
    [PERCENTAGE_OR_AMOUNT]: '',
  };
}

export function transformFeeForApi(fee, currency) {
  const hasPercentage = isFilled(fee.percentage);
  const hasAmount = isFilled(fee.amount);

  return {
    ...omit(fee, EDITOR_KEYS),
    amount: hasAmount ? formatPriceInput(fee.amount, currency) : null,
    percentage: hasPercentage ? parseFloat(String(fee.percentage).replace(',', '.')) : null,
    multiplyByQuantity: hasAmount ? fee.multiplyByQuantity : false,
    multiplyByTickets: hasAmount ? fee.multiplyByTickets : false,
  };
}

function describePaymentMethods(methods = []) {
  const names = methods.map(
    method => paymentMethods.find(option => option.value === method)?.name ?? method,
  );
  if (names.length === 1) return `${names[0]} only`;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.length} payment methods`;
}

// Takes form values (amount in major units), as the editor holds them.
export function getFeeSummary(fee, page) {
  const hasPercentage = isFilled(fee.percentage);
  const hasAmount = isFilled(fee.amount);
  if (!hasPercentage && !hasAmount) return [];

  const currency = getPageCurrency(page);
  const amount = hasAmount
    ? formatPrice({ amount: formatPriceInput(fee.amount, currency), currency })
    : '';
  const percentage = hasPercentage ? `${String(fee.percentage).replace(',', '.')}%` : '';

  let fixed = amount;
  if (page.type === 'checkout' && fee.multiplyByQuantity) {
    fixed = `${amount} per item`;
  } else if (page.type === 'event' && fee.multiplyByTickets) {
    fixed = `${amount} per ticket`;
  }

  let charge;
  if (hasPercentage && hasAmount) {
    charge = `${percentage} + ${fixed}`;
  } else if (hasPercentage) {
    charge = percentage;
  } else {
    charge = fixed;
  }

  return fee.applyToSpecificPaymentMethods && fee.paymentMethods?.length
    ? [charge, describePaymentMethods(fee.paymentMethods)]
    : [charge];
}

// The both-blank error belongs to neither input, so it gets its own path and shows once under the pair.
export const feeValidationSchema = Yup.object()
  .shape({
    name: Yup.string().required('Fee name is required'),
    amount: Yup.number().nullable().moreThan(0, 'Amount must be more than 0'),
    percentage: Yup.number().nullable().moreThan(0, 'Percentage must be more than 0'),
  })
  .test(
    'percentage-or-amount',
    'Enter a percentage, a fixed amount or both',
    (values, context) =>
      isFilled(values?.percentage) ||
      isFilled(values?.amount) ||
      context.createError({ path: PERCENTAGE_OR_AMOUNT }),
  );
